import { useMemo, useState } from "react";
import { uiNotification } from "../../../../../../services/UINotificationService";
import { useProductDetailedLogsQuery } from "../../../../../../api/queries/useProductDetailedLogsQuery";
import { useDownloadProductDetailedLogsMutation } from "../../../../../../api/mutations/useDownloadProductDetailedLogsMutation";
import { composeDownloadDataUrl } from "../../../../../../utilities/api";
import { useDebouncedState } from "../../../../../../utilities/hooks/useDebouncedState";

const INITIAL_PERIOD = {
  from: undefined,
  to: undefined,
};

const DEFAULT_CLINIC = {
  label: "All Clinics",
  value: "",
};

export function useLogs({ productId, clinics, inventoryType }) {
  const { 0: period, 1: setPeriod } = useState(INITIAL_PERIOD);
  const { 0: batch, 1: debouncedBatch, 2: setBatch } = useDebouncedState("");
  const { 0: selectedClinics, 1: setSelectedClinics } = useState(clinics);

  const isAllClinicsSelected = selectedClinics.length === clinics.length;

  const payload = {
    productId: Number(productId),
    pageSize: 15,
    dateFrom: period.from,
    dateTo: period.to,
    clinicIds: isAllClinicsSelected
      ? clinics.map((c) => c.id)
      : selectedClinics.map((c) => c.value),
    allClinics: isAllClinicsSelected,
    batch: debouncedBatch,
  };

  const downloadMutation = useDownloadProductDetailedLogsMutation({
    onError: () => {
      uiNotification.error(
        "Unable to download detailed logs. Try again later.",
      );
    },
    onSuccess: (res) => {
      window.open(composeDownloadDataUrl(res.fileName), "_blank");
    },
  });

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useProductDetailedLogsQuery({
      payload,
      options: {
        staleTime: 0,
        onError: () => {
          uiNotification.error(
            "Unable to fetch detailed logs. Try again later.",
          );
        },
      },
    });

  const logs = useMemo(() => {
    return data?.pages?.flatMap((p) => p.logs) || [];
  }, [data?.pages]);

  const isEmpty = !isLoading && logs.length === 0;

  const isLotNumberColVisible = !["retail", "medical_supplies"].includes(
    inventoryType,
  );

  const clinicOptions = useMemo(
    () => [
      DEFAULT_CLINIC,
      ...clinics.map((clinic) => ({
        label: clinic.clinic_name,
        value: clinic.id,
      })),
    ],
    [clinics],
  );

  return {
    value: logs,
    isLoading,
    isEmpty,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLotNumberColVisible,
    period: {
      value: period,
      update: setPeriod,
      reset: () => setPeriod(INITIAL_PERIOD),
    },
    clinics: {
      options: clinicOptions,
      isAllSelected: isAllClinicsSelected,
      value: isAllClinicsSelected
        ? DEFAULT_CLINIC
        : selectedClinics[0] || DEFAULT_CLINIC,
      update: (next) => {
        if (next.value === DEFAULT_CLINIC.value) {
          setSelectedClinics(clinics);
        } else {
          setSelectedClinics([next]);
        }
      },
      reset: () => setSelectedClinics(clinics),
    },
    download: {
      isLoading: downloadMutation.isLoading,
      initiate: () => {
        downloadMutation.mutate({
          ...payload,
          page: data?.pages?.[data?.pages?.length - 1]?.page,
        });
      },
    },
    batch: {
      value: batch,
      update: setBatch,
    },
  };
}
